// SPDX-License-Identifier: AGPL-3.0-or-later

import {isElectronPlatform} from '@app/features/platform/types/Platform';
import AdvancedSettings from '@app/features/user/state/AdvancedSettings';
import {resetNativeHardwareEncoderCapabilities} from '@app/features/voice/utils/NativeHardwareEncoderCapabilities';

let nativeSupportState: 'unknown' | 'checking' | 'supported' | 'unsupported' = 'unknown';
let nativeSupportPromise: Promise<boolean> | null = null;
let nativeVoiceEngineSuppressed = false;

function getNativeVoiceEngineBridge() {
	if (!isElectronPlatform()) return null;
	return window.electron?.voiceEngine ?? null;
}

function shouldSelectNativeFromCachedState(): boolean {
	if (nativeVoiceEngineSuppressed) return false;
	return AdvancedSettings.nativeVoiceEngineEnabled;
}

export function isNativeVoiceEngineSelected(): boolean {
	const bridge = getNativeVoiceEngineBridge();
	if (!bridge) return false;
	if (nativeSupportState === 'unknown' && shouldSelectNativeFromCachedState()) {
		void shouldUseNativeVoiceEngine();
	}
	return nativeSupportState === 'supported' && shouldSelectNativeFromCachedState();
}

export function isNativeVoiceEngineSelectionPending(): boolean {
	if (nativeVoiceEngineSuppressed) return false;
	return (
		Boolean(getNativeVoiceEngineBridge()) &&
		(nativeSupportState === 'checking' ||
			(AdvancedSettings.nativeVoiceEngineEnabled && nativeSupportState === 'unknown'))
	);
}

export async function shouldUseNativeVoiceEngine(): Promise<boolean> {
	if (nativeVoiceEngineSuppressed) return false;
	const bridge = getNativeVoiceEngineBridge();
	if (!bridge) return false;
	if (nativeSupportState === 'supported') {
		return AdvancedSettings.nativeVoiceEngineEnabled;
	}
	if (nativeSupportState === 'unsupported') return false;
	if (nativeSupportPromise) return nativeSupportPromise;
	nativeSupportState = 'checking';
	nativeSupportPromise = bridge
		.isSupported()
		.then(async (supported) => {
			nativeSupportState = supported ? 'supported' : 'unsupported';
			if (!supported) return false;
			return AdvancedSettings.nativeVoiceEngineEnabled;
		})
		.catch(() => {
			nativeSupportState = 'unsupported';
			return false;
		})
		.finally(() => {
			nativeSupportPromise = null;
		});
	return nativeSupportPromise;
}

export function suppressNativeVoiceEngineSelectionForFallback(): void {
	nativeVoiceEngineSuppressed = true;
}

export function resetNativeVoiceEngineSelectionForTesting(): void {
	nativeSupportState = 'unknown';
	nativeSupportPromise = null;
	nativeVoiceEngineSuppressed = false;
	resetNativeHardwareEncoderCapabilities();
}
